import { createFeatureSelector, createSelector, MemoizedSelector } from "@ngrx/store";
import { BrandCategorySummaries, Customer, OrderSummary, PaymentInfo } from "src/app/features/shop/models/entities";
import { Order } from "src/app/shared/models/entities";
import { BaseAction } from "../store.actions";
import * as rootReducers from '../store.reducers';

export interface  CartSummary {
  amount: number | undefined;
  items: number | undefined;
  disable: boolean | undefined;
}


export interface Cart {
  order?: Order; // order completo
  customer?: Customer; // cliente
  summary?: CartSummary; // riepilogo per carrello-component
  // cartSummaryDetail?: number;
}

/**
 * Aggregates refreshed after a line-level mutation. Deliberately excludes `items`/`details`: the
 * point of the narrow sync is not to re-download the order's lines.
 */
export interface CartTotals {
  /**
   * The order these aggregates describe. The cart panel is a persistent sidenav, so a sync fired
   * on one order can land after the user has navigated to another; without this the late response
   * would patch the wrong order's summary.
   */
  orderId: number;
  brandCategorySummaries: BrandCategorySummaries[];
  total: number;
  count: number;
  payment: PaymentInfo;
}

export interface Shop {
  requestCartId: any;
  cartLoading: boolean;
  order: Order | null;
  currentCart: Cart | null;
  cartOrderSummary: OrderSummary | null;
  cartTotals: CartTotals | null;
}

export interface State extends rootReducers.State {
  shop: Shop;
}

const initialState: Shop = {
  requestCartId: null,
  cartLoading: false,
  order: null,
  currentCart: {} || null,
  cartOrderSummary: null,
  cartTotals: null
};

export enum ShopActionType {
  CartSelect = '[Shop] CartSelect',
  CartLoaded= '[Shop] CartLoaded',
  CartChange= '[Shop] CartChange',
  CartItemSynced = '[Shop] CartItemSynced',
  CartTotalsLoaded = '[Shop] CartTotalsLoaded',
}

export function shopReducer(state: Shop = initialState, action: BaseAction): Shop {
  switch (action.type) {
    case ShopActionType.CartSelect: {
      return {
        ...state,
        requestCartId: action.payload,
        cartLoading: true,
        currentCart: null
      } ;
    }
    case ShopActionType.CartLoaded: {

      return {
        ...state,  order: action.payload
      } ;

    }
    case ShopActionType.CartChange: {

      return {
        ...state,  currentCart: action.payload, cartLoading: false
      } ;

    }
    case ShopActionType.CartItemSynced: {
      // A line changed inside the SAME order. Unlike CartSelect this must not null currentCart or
      // raise cartLoading — doing so blanks the panel and tears down every rendered item.
      const incoming: Order | null | undefined = action.payload;
      if (!incoming) {
        return state;
      }
      // MERGED over the displayed order, never a wholesale replace: the header renders
      // orderStatusId/paymentStatus and hands the order to app-order-descr, which reads
      // customer.name/surname, description, createdBy and dateCreated. The PUT response does
      // carry the full refreshed entity today — this keeps a leaner one from blanking the header.
      // The merge base is the previous order ONLY when it's the SAME order (id match). The cart
      // panel is a persistent sidenav, so a sync for order A can land after the user has already
      // navigated to order B; without the id check, A's customer/description would bleed into
      // B's header under B's id — worse than the blank field this merge exists to prevent. This
      // is not redundant defensiveness: delete it and cross-order syncs silently mix state.
      const previous = state.currentCart?.order;
      const base = previous && previous.id === incoming.id ? previous : {};
      const order: Order = { ...base, ...incoming } as Order;
      return {
        ...state,
        order,
        currentCart: {
          order,
          customer: order?.customer,
          summary: { amount: order?.['total'], items: order?.count, disable: order?.obsolete }
        }
      };
    }
    case ShopActionType.CartTotalsLoaded: {
      return {
        ...state, cartTotals: action.payload
      };
    }
    default:
      return state;
  }

}

export const FEATURE_NAME = 'shop';
export const selectShop = createFeatureSelector<State, Shop>(
    FEATURE_NAME
);

export const selectRequestCartId: MemoizedSelector<State, any>  = createSelector(selectShop, (state: Shop) => state.requestCartId);
export const selectCurrentCart: MemoizedSelector<State, Cart | null>  = createSelector(selectShop, (state: Shop) => state.currentCart);
export const selectCartLoading: MemoizedSelector<State, boolean>  = createSelector(selectShop, (state: Shop) => state.cartLoading);
export const selectCurrentOrder: MemoizedSelector<State, Order | undefined>  = createSelector(selectCurrentCart, (state: Cart | null) => state?.order);
export const selectCurrentCustomer: MemoizedSelector<State, Customer | undefined>  = createSelector(selectCurrentCart, (state: Cart | null) => state?.customer);
export const selectCartTotals: MemoizedSelector<State, CartTotals | null>  = createSelector(selectShop, (state: Shop) => state.cartTotals);
